import React, {useState, memo} from "react";
import ListItem from "@material-ui/core/ListItem";
import FolderIcon from "@material-ui/icons/Folder";
import ListItemText from "@material-ui/core/ListItemText";
import {Checkbox} from "@material-ui/core";
import {Asset, changeFolder, toggleFolderVisibility} from "../redux/assets-slice";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import List from "@material-ui/core/List";
import {AddAssetList, AssetItem} from ".";
import {useDispatch} from 'react-redux';
import {
  DenseIcon,
  DenseMenu,
  getAnchorPosition,
  initialMenuState,
  MenuDivider,
  NestedMenu,
  PromptDialog,
  TextDialog
} from '../components';
import {MenuItem, Typography} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from '@material-ui/icons/Add';
import {deleteAssets} from '../redux/assets-slice';
import {deleteFolder, Folder, renameFolder} from '../redux/folders-slice';
import { isStaticSite } from "../electron-modules";
import { Viewer } from "../viewer/js/main";
import { useTranslation } from "react-i18next";
import { addFilePaths, isAssetFile } from "../utilities";
import slash from "slash";

export const assetFolderClass = "asset-folder";

export const AssetFolder = memo((props: any) => {
  const viewer = props.viewer as Viewer;
  const folder = props.folder as Folder;
  const assets = props.assets as Asset[];

  const {draggingAsset, setDraggingAsset} = props;

  const dispatch = useDispatch();
  const {t} = useTranslation();

  const [expanded, setExpanded] = useState(true);
  const [dragDropHover, setDragDropHover] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [menuState, setMenuState] = useState({...initialMenuState});

  const updateAssetFolder = (assetID) => {
    dispatch(changeFolder({
      assetID,
      folderID: folder.id
    }));
  };

  const addAssetsToFolder = (event) => {
    const files = [...event.dataTransfer.files]
      .map(file => slash(file.path))
      .filter(isAssetFile);

    addFilePaths(dispatch, files, folder.id);
  };

  const handleClick = (event) => {
    const isCheckBox = (event.target.type === "checkbox");
    if (isCheckBox) return;

    setExpanded(!expanded);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragDropHover(true);
  };

  const handleDragLeave = (event) => {
    setDragDropHover(false);
  };

  const handleDrop = (event) => {
    setDragDropHover(false);

    if (draggingAsset) {
      updateAssetFolder(draggingAsset);
    } else {
      addAssetsToFolder(event);
    }
  };

  const handleClose = () => {
    setMenuState(initialMenuState);
  };

  const handleRenameCancel = () => {
    setRenameOpen(false);
  };

  const handleRenameSubmit = name => {
    dispatch(renameFolder({id: folder.id, name}));
    setRenameOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
  };

  const handleDeleteSubmit = () => {
    setDeleteOpen(false);
    dispatch(deleteAssets(folder.id));
    dispatch(deleteFolder(folder.id));
  };

  const handleContextMenu = (event) => {
    event.preventDefault();
    setMenuState({
      mouseX: event.clientX,
      mouseY: event.clientY,
    });
  };

  const handleDeleteClick = () => {
    setMenuState(initialMenuState);
    setDeleteOpen(true);
  };

  const handleRenameClick = () => {
    setMenuState(initialMenuState);
    setRenameOpen(true);
  };

  const toggleCheckboxes = () => {
    let newVisibility;
    if (numVisible > 0 && numHidden > 0) {
      newVisibility = true;
    } else if (numVisible > 0) {
      newVisibility = false;
    } else {
      newVisibility = true;
    }

    dispatch(toggleFolderVisibility({
      folderID: folder.id,
      visible: newVisibility
    }));
  };

  const menuOpen = (menuState.mouseY !== null) && !isStaticSite;
  const anchorPosition = getAnchorPosition(menuState);
  const numVisible = assets.filter(x => x.visible).length;
  const numHidden = assets.filter(x => !x.visible).length;

  // Folder is visible if it is user made, or if it contains assets
  const visible = folder.isDefault ? assets.length > 0 : true;

  const localizedFolderName = t(folder.name);

  return (
    <React.Fragment>
      {visible && (<React.Fragment>
        <ListItem
          button
          dense
          className={assetFolderClass}
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onContextMenu={handleContextMenu}
          selected={dragDropHover}
        >
          <DenseIcon>
            <FolderIcon fontSize="small"/>
          </DenseIcon>
          <ListItemText primary={localizedFolderName}/>

          {(assets.length > 0) && (<React.Fragment>
            {/* Show / hide contents */}
            {expanded ? <ExpandLess/> : <ExpandMore/>}

            {/* Toggle all folder assets */}
            <Checkbox
              edge="end"
              size="small"
              checked={numVisible > 0}
              onChange={toggleCheckboxes}
              indeterminate={numVisible > 0 && numHidden > 0}
            />
          </React.Fragment>)}

        </ListItem>
      </React.Fragment>)}

      {visible && expanded &&  (<React.Fragment>
        <List component="div" disablePadding dense>
          {assets.map(asset => <AssetItem
            key={asset.id}
            viewer={viewer}
            asset={asset}
            setDraggingAsset={setDraggingAsset}
          />)}
        </List>
      </React.Fragment>)}

      <DenseMenu
        open={menuOpen}
        onClose={handleClose}
        anchorPosition={anchorPosition}
      >
        {/** New asset submenu */}
        {!isStaticSite && (<NestedMenu
          dense
          text={t('folder.add_assets')}
          icon={<AddIcon fontSize="small"/>}
          parentMenuOpen={menuOpen}
        >
          <AddAssetList
            folderID={folder.id}
            closeContextMenu={handleClose}
          />
        </NestedMenu>)}

        {/** Functions submenu */}

        {/*
        {!isStaticSite && <FunctionsMenu
          viewer={viewer}
          assets={assets}
          contextOpen={menuOpen}
          closeContextMenu={handleClose}
        />}
        */}

        {!isStaticSite && <MenuDivider/>}

        {/** Rename asset */}
        <MenuItem onClick={handleRenameClick}>
          <DenseIcon>
            <EditIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">
            {t("folder.rename")}
          </Typography>
        </MenuItem>

        {/** Delete asset */}
        <MenuItem onClick={handleDeleteClick}>
          <DenseIcon>
            <DeleteIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">
            {t("folder.delete")}
          </Typography>
        </MenuItem>

      </DenseMenu>

      <TextDialog
        open={renameOpen}
        onCancel={handleRenameCancel}
        onSubmit={handleRenameSubmit}
        title={t("folder.rename-title")}
        prompt={t("folder.rename-prompt")}
        default={localizedFolderName}
        label={t("folder.rename-label")}
      />

      <PromptDialog
        open={deleteOpen}
        onCancel={handleDeleteCancel}
        onSubmit={handleDeleteSubmit}
        title={t("folder.remove-title")}
        prompt={t("folder.remove-prompt")}
        button={t("buttons.delete")}
      />


    </React.Fragment>
  );
});
